import { useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Field } from "@/components/FormBits";

export function LoginForm({ mode = "login", onDone, testPrefix = "login" }) {
  const { login, register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = mode === "login" ? await login(f.email, f.password) : await register(f.name, f.email, f.password);
      onDone && onDone(u);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      {mode === "register" && <Field label="Name" id={`${testPrefix}-name-input`} required value={f.name} onChange={set("name")} />}
      <Field label="Email" id={`${testPrefix}-email-input`} type="email" required value={f.email} onChange={set("email")} />
      <Field label="Password" id={`${testPrefix}-password-input`} type="password" required minLength={mode === "register" ? 6 : undefined} value={f.password} onChange={set("password")} />
      {err && <p data-testid={`${testPrefix}-error`} className="text-sm text-rose-400">{err}</p>}
      <button data-testid={`${testPrefix}-submit-button`} disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#fde68a] font-bold text-zinc-950 disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} {mode === "login" ? "Log in" : "Create account"}
      </button>
    </form>
  );
}

export default function Login() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const next = params.get("next") || "/";
  if (user) return <Navigate to={next} replace />;
  return (
    <div className="page max-w-md">
      <h1 className="font-heading text-5xl text-white">{mode === "login" ? "Welcome back" : "Join for free"}</h1>
      <p className="mt-2 text-zinc-400">Save your favorite happy hour spots and see them on any device.</p>
      <div className="panel mt-6 p-6">
        <Tabs value={mode} onValueChange={setMode} className="mb-5">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="login-tab">Log in</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Create account</TabsTrigger>
          </TabsList>
        </Tabs>
        <LoginForm key={mode} mode={mode} testPrefix={mode} onDone={() => nav(next, { replace: true })} />
      </div>
    </div>
  );
}
